import { dataImageCanvas, ScreenImageCanvas, registerGlobeImageLayers } from './data-image-render.js';
import type { Map as GlobeMap } from 'maplibre-gl';
import { dataIconCanvas } from './data-icon-style.js';
import { dataFeatureStyle } from '../shared/data-layer-fields.js';
import { visibleDataFeatures } from '../shared/data-layer-display.js';
import type { DataLayer } from '../shared/data-layer.js';
import { getGlobeViewer } from './globe-view.js';
import { GlobeSource, globeFeature, globeBounds } from './globe-source.js';
import type { Feature, Geometry } from 'geojson';

export class GlobeDataLayers {
  private readonly ground = new Map<string, { map: GlobeMap; ids: string[]; ready: Promise<void> }>();
  private screens?: { map: GlobeMap; canvas: ScreenImageCanvas };

  private readonly entries = new Map<string, { layer: DataLayer; source?: GlobeSource; features: Feature[] }>();
  private readonly onViewChanged = () => { for (const entry of [...this.entries.values()]) this.replace(entry.layer); };
  constructor() { window.addEventListener('globe-view-changed', this.onViewChanged); }
  replace(layer: DataLayer): void {
    this.remove(layer.id);
    const features: Feature[] = [];
    for (const feature of visibleDataFeatures(layer)) {
      if (!feature.geometry || (feature.image && (!feature.image.visible || feature.image.kind === 'ground'))) continue;
      // SAFETY: Data layers validate GeoJSON geometry at the import and storage boundaries.
      const geometry = feature.geometry as Geometry;
      const style = dataFeatureStyle(feature, layer);
      const properties = { radius: style.pointRadius ?? 6, pointOutlineColor: style.pointOutlineColor ?? '#ffffff', color: style.color, width: style.width ?? 3, opacity: style.opacity ?? 1, fill: style.fillColor ?? style.color, fillOpacity: style.fillOpacity ?? 0.2 };
      features.push(globeFeature(feature.id, geometry, properties));
      const rings = geometry.type === 'Polygon' ? geometry.coordinates : geometry.type === 'MultiPolygon' ? geometry.coordinates.flat() : [];
      rings.forEach((coordinates, index) => features.push(globeFeature(`${feature.id}-outline-${index}`, { type: 'LineString', coordinates }, properties)));
    }
    const map = getGlobeViewer();
    const source = map ? new GlobeSource(map, `data-${layer.id}`) : undefined;
    source?.set(features); source?.visible(layer.visible);
    const entry = { layer, source, features };
    this.entries.set(layer.id, entry);
    if (source) {
      void this.renderIcons(entry);
      this.installImages(layer, source.map);
      this.orderVectors(source.map);
    }
  }
  private installImages(layer: DataLayer, map: GlobeMap): void {
    if (this.screens?.map !== map) {
      if (this.screens) { this.screens.map.off('render', this.screens.canvas.draw); this.screens.canvas.destroy(); }
      const canvas = new ScreenImageCanvas(map.getContainer(), () => [...this.entries.values()].map(entry => entry.layer));
      this.screens = { map, canvas }; map.on('render', canvas.draw);
      registerGlobeImageLayers(map, () => [...this.entries.values()].map(entry => entry.layer), async () => { await Promise.all([...this.ground.values()].filter(entry => entry.map === map).map(entry => entry.ready)); });
    }
    this.screens.canvas.refresh();
    const ids: string[] = [];
    const entry = { map, ids, ready: Promise.resolve() };
    this.ground.set(layer.id, entry);
    const ground = visibleDataFeatures(layer).flatMap(feature => feature.image?.kind === 'ground' && feature.image.visible ? [{ id: feature.id, image: feature.image }] : []).sort((a, b) => a.image.drawOrder - b.image.drawOrder);
    entry.ready = Promise.all(ground.map(async item => ({ ...item, canvas: await dataImageCanvas(item.image) }))).then(items => {
      if (this.ground.get(layer.id) !== entry || getGlobeViewer() !== map) return;
      for (const { id: featureId, image, canvas } of items) {
        const id = `data-ground-${layer.id}-${featureId}`;
        map.addSource(id, { type: 'image', url: canvas.toDataURL('image/png'), coordinates: image.corners });
        map.addLayer({ id, type: 'raster', source: id, layout: { visibility: layer.visible ? 'visible' : 'none' }, paint: { 'raster-opacity': image.opacity, 'raster-fade-duration': 0 } }, map.getLayer(`data-${layer.id}-fill`) ? `data-${layer.id}-fill` : undefined);
        entry.ids.push(id);
      }
      this.orderImages(map);
    });
    void entry.ready.catch(error => console.warn('Ground image display failed:', error));
  }
  private orderVectors(map: GlobeMap): void {
    for (const { layer } of [...this.entries.values()].sort((a, b) => a.layer.position - b.layer.position)) {
      for (const suffix of ['fill', 'line', 'point', 'image', 'polar']) {
        const id = `data-${layer.id}-${suffix}`;
        if (map.getLayer(id)) map.moveLayer(id);
      }
    }
  }
  private orderImages(map: GlobeMap): void {
    const layers = [...this.entries.values()].sort((a, b) => a.layer.position - b.layer.position);
    const vectors = new Set(layers.flatMap(({ layer }) => [`data-${layer.id}-fill`, `data-${layer.id}-line`, `data-${layer.id}-point`, `data-${layer.id}-image`, `data-${layer.id}-polar`]));
    const firstVector = map.getStyle().layers.find(layer => vectors.has(layer.id))?.id;
    if (firstVector) for (const { layer } of layers) for (const id of this.ground.get(layer.id)?.ids ?? []) if (map.getLayer(id)) map.moveLayer(id, firstVector);
  }
  private async renderIcons(entry: { layer: DataLayer; source?: GlobeSource; features: Feature[] }): Promise<void> {
    const source = entry.source;
    if (!source) return;
    const images = new Map<string, string>();
    const renderedFeatures = new Map(entry.features.map((feature) => [feature.id, feature]));
    const icons = visibleDataFeatures(entry.layer).filter((feature) => feature.sourceStyle?.icon ?? entry.layer.style.icon);
    for (const feature of icons) {
      const icon = (feature.sourceStyle?.icon ?? entry.layer.style.icon)!;
      const key = JSON.stringify(icon);
      let image = images.get(key);
      if (!image) {
        const canvas = await dataIconCanvas(icon).catch(() => null);
        if (this.entries.get(entry.layer.id) !== entry || source.map !== getGlobeViewer()) return;
        if (!canvas) continue;
        image = source.image(String(images.size), canvas);
        images.set(key, image);
      }
      const rendered = renderedFeatures.get(feature.id);
      if (rendered?.properties) {
        rendered.properties.image = image;
        rendered.properties.opacity = 1;
      }
    }
    if (this.entries.get(entry.layer.id) === entry && source.map === getGlobeViewer()) source.set(entry.features);
  }
  remove(id: string): void {
    const ground = this.ground.get(id);
    if (ground && ground.map === getGlobeViewer()) for (const sourceId of ground.ids) { if (ground.map.getLayer(sourceId)) ground.map.removeLayer(sourceId); if (ground.map.getSource(sourceId)) ground.map.removeSource(sourceId); }
    this.ground.delete(id);
    const entry = this.entries.get(id); if (entry?.source && entry.source.map === getGlobeViewer()) entry.source.destroy(); this.entries.delete(id); this.screens?.canvas.refresh(); }
  setVisible(id: string, visible: boolean): void { const entry = this.entries.get(id); if (entry) { entry.layer.visible = visible; entry.source?.visible(visible); }
    const ground = this.ground.get(id);
    if (ground && ground.map === getGlobeViewer()) for (const sourceId of ground.ids) ground.map.setLayoutProperty(sourceId, 'visibility', visible ? 'visible' : 'none');
    this.screens?.canvas.refresh();
  }
  fit(id: string): void { const entry = this.entries.get(id); if (!entry?.source) return; const bounds: [[number, number], [number, number]] | null = entry.layer.bounds ? [[entry.layer.bounds[0], entry.layer.bounds[1]], [entry.layer.bounds[2], entry.layer.bounds[3]]] : globeBounds(entry.features); if (bounds) entry.source.map.fitBounds(bounds, { padding: 60, maxZoom: 16, duration: 300 }); }
  destroy(): void { if (this.screens) { this.screens.map.off('render', this.screens.canvas.draw); this.screens.canvas.destroy(); } window.removeEventListener('globe-view-changed', this.onViewChanged); for (const id of this.entries.keys()) this.remove(id); }
}
